import { useEffect, useMemo, useState } from "react";
import { SIM } from "@/constants/testIds";
import { simulate, fmtMoney, fmtNumber } from "@/lib/simulator";
import { DEFAULT_ADVANCED, RANKS, packageById } from "@/lib/compPlan";
import {
  encodeScenario, decodeScenario, buildShareUrl, readScenarioFromHash,
} from "@/lib/share";
import { Slider } from "@/components/ui/slider";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";
import PackagePicker from "@/components/simulator/PackagePicker";
import StepCard from "@/components/simulator/StepCard";
import ResultsPanel from "@/components/simulator/ResultsPanel";
import OrderFlow from "@/components/simulator/OrderFlow";
import ShareModal from "@/components/simulator/ShareModal";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import {
  Share, ArrowClockwise, CaretDown, SlidersHorizontal,
  ArrowCounterClockwise, Sparkle,
} from "@phosphor-icons/react";

const DEFAULTS = {
  myPackage: null,             // step 1 not started
  directs: 5,
  directPackage: "bronze",
  l2PerDirect: 2,
  l2Package: "bronze",
  l3PerL2: 2,
  l3Package: "bronze",
  l4PerL3: 2,
  l4Package: "bronze",
  l5PerL4: 2,
  l5Package: "bronze",
  l6PerL5: 2,
  l6Package: "bronze",
  l7PerL6: 2,
  l7Package: "bronze",
  l8PerL7: 2,
  l8Package: "bronze",
  l9PerL8: 2,
  l9Package: "bronze",
  l10PerL9: 2,
  l10Package: "bronze",
  downlinePackage: "bronze",
  duplicationPct: 35,
  duplicationMultiplier: DEFAULT_ADVANCED.duplicationMultiplier,
  branchingFactor: DEFAULT_ADVANCED.branchingFactor,
  retentionRate: DEFAULT_ADVANCED.retentionRate,
};

export default function Simulator() {
  const { t } = useI18n();
  const [inputs, setInputs] = useState(DEFAULTS);
  // How many downline levels the user has explicitly configured (0..5).
  //   0 = package picked, no levels yet   ->  results panel shows placeholder
  //   1 = L1 configured (Step 2 completed)
  //   ...
  //   5 = all L1-L5 configured (Step 6 completed)
  const [configuredLevels, setConfiguredLevels] = useState(0);
  const [activeStep, setActiveStep] = useState(1);
  const [calculateDeeper, setCalculateDeeper] = useState(false);
  const [deeperOpen, setDeeperOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [showOrderFlow, setShowOrderFlow] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");

  const set = (patch) => setInputs((s) => ({ ...s, ...patch }));
  const startedFlow = inputs.myPackage !== null;

  // Hydrate scenario from URL hash on first mount so shared links restore the
  // exact projection.
  useEffect(() => {
    const scenario = readScenarioFromHash();
    if (!scenario) return;
    // Merge over defaults so any missing fields fall back safely.
    setInputs((prev) => ({ ...prev, ...scenario.inputs }));
    setConfiguredLevels(scenario.configuredLevels || 0);
    setCalculateDeeper(!!scenario.calculateDeeper);
    // Auto-advance the stepper to the next unconfigured level
    const nextStep = Math.min(11, (scenario.configuredLevels || 0) + 2);
    setActiveStep(nextStep);
    toast.success(t("share.toast.loaded.title"), {
      description: t("share.toast.loaded.desc"),
    });
  }, []);

  // Compute only using levels the user has configured so far
  const result = useMemo(() => {
    if (!inputs.myPackage) return null;
    return simulate({ ...inputs, configuredLevels, calculateDeeper });
  }, [inputs, configuredLevels, calculateDeeper]);

  const resetAll = () => {
    setInputs(DEFAULTS);
    setConfiguredLevels(0);
    setActiveStep(1);
    setCalculateDeeper(false);
    setDeeperOpen(false);
    setAdvancedOpen(false);
    setShowOrderFlow(false);
    // Also strip any share payload from the URL so a refresh doesn't hydrate it back
    if (typeof window !== "undefined" && window.location.hash) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
  };

  const rankIdx = result ? RANKS.findIndex((r) => r.rank === result.rank.rank) : -1;

  // Step states — Step 1 = package, Steps 2-11 = Levels 1-10, then deeper toggle.
  const stepState = (n) => {
    if (!startedFlow) return n === 1 ? "active" : "locked";
    if (n === 1) return activeStep === 1 ? "active" : "done";
    const requiredConfigured = n - 2; // step 2 needs 0, step 3 needs 1, etc.
    if (configuredLevels < requiredConfigured) return "locked";
    return n === activeStep ? "active" : "done";
  };

  const configureAndAdvance = (levelJustConfigured) => {
    setConfiguredLevels((c) => Math.max(c, levelJustConfigured));
    setActiveStep(Math.min(11, levelJustConfigured + 2));
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 pt-8 pb-24" data-testid={SIM.container}>
      {/* HEADER */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4 fade-up">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400">
            {t("sim.eyebrow")}
          </div>
          <h1 className="mt-1 font-display text-3xl md:text-4xl font-semibold tracking-tight">
            {t("sim.title.pre")}{" "}
            <span className="grad-text">{t("sim.title.highlight")}</span>
          </h1>
          <p className="mt-2 text-sm text-slate-400 max-w-xl">
            {t("sim.subtitle")}{" "}
            <a
              href="https://u.center/docs"
              target="_blank"
              rel="noreferrer"
              className="text-pink-400 hover:text-pink-300 underline decoration-dotted"
            >
              u.center/docs
            </a>.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            data-testid={SIM.resetBtn}
            onClick={resetAll}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs text-slate-300 border border-white/10 hover:border-white/25 hover:bg-white/[0.03]"
          >
            <ArrowClockwise weight="bold" className="h-3.5 w-3.5" /> {t("sim.reset")}
          </button>
          <button
            data-testid={SIM.shareBtn}
            onClick={() => {
              if (!startedFlow) {
                toast(t("share.toast.noPkg.title"), {
                  description: t("share.toast.noPkg.desc"),
                });
                return;
              }
              const payload = encodeScenario({ inputs, configuredLevels, calculateDeeper });
              setShareUrl(buildShareUrl(payload));
              setShareOpen(true);
            }}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs text-white border border-white/10 bg-white/[0.05] hover:bg-white/[0.09]"
          >
            <Share weight="bold" className="h-3.5 w-3.5" /> {t("sim.share")}
          </button>
        </div>
      </div>

      <ShareModal
        open={shareOpen}
        onOpenChange={setShareOpen}
        url={shareUrl}
        snapshot={result ? {
          youEarn: fmtMoney(result.totals.totalCash),
          teamPayout: fmtMoney(result.totals.teamCash || 0),
          rank: result.rank.rank,
        } : null}
      />

      {/* MAIN GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* LEFT: Steps */}
        <div className="lg:col-span-5 space-y-3">
          <StepOne
            state={stepState(1)}
            inputs={inputs}
            set={set}
            onAdvance={() => configureAndAdvance(0)}
            onEdit={() => setActiveStep(1)}
          />
          <StepTwo
            state={stepState(2)}
            inputs={inputs}
            set={set}
            onAdvance={() => configureAndAdvance(1)}
            onEdit={() => setActiveStep(2)}
          />
          <LevelStep
            step={3}
            level={2}
            state={stepState(3)}
            inputs={inputs}
            set={set}
            testId={SIM.step3}
            countKey="l2PerDirect"
            pkgKey="l2Package"
            countSliderId={SIM.l2PerDirectSlider}
            countValueId={SIM.l2PerDirectValue}
            parentLabel="direct"
            parentCount={inputs.directs}
            onAdvance={() => configureAndAdvance(2)}
            onEdit={() => setActiveStep(3)}
          />
          <LevelStep
            step={4}
            level={3}
            state={stepState(4)}
            inputs={inputs}
            set={set}
            testId={SIM.step4}
            countKey="l3PerL2"
            pkgKey="l3Package"
            countSliderId={SIM.l3PerL2Slider}
            countValueId={SIM.l3PerL2Value}
            parentLabel="Level 2 member"
            parentCount={inputs.directs * inputs.l2PerDirect}
            onAdvance={() => configureAndAdvance(3)}
            onEdit={() => setActiveStep(4)}
          />
          <LevelStep
            step={5}
            level={4}
            state={stepState(5)}
            inputs={inputs}
            set={set}
            testId={SIM.step5}
            countKey="l4PerL3"
            pkgKey="l4Package"
            countSliderId={SIM.l4PerL3Slider}
            countValueId={SIM.l4PerL3Value}
            parentLabel="Level 3 member"
            parentCount={inputs.directs * inputs.l2PerDirect * inputs.l3PerL2}
            onAdvance={() => configureAndAdvance(4)}
            onEdit={() => setActiveStep(5)}
          />
          <LevelStep
            step={6}
            level={5}
            state={stepState(6)}
            inputs={inputs}
            set={set}
            testId={SIM.step6}
            countKey="l5PerL4"
            pkgKey="l5Package"
            countSliderId={SIM.l5PerL4Slider}
            countValueId={SIM.l5PerL4Value}
            parentLabel="Level 4 member"
            parentCount={inputs.directs * inputs.l2PerDirect * inputs.l3PerL2 * inputs.l4PerL3}
            onAdvance={() => configureAndAdvance(5)}
            onEdit={() => setActiveStep(6)}
          />
          <LevelStep
            step={7}
            level={6}
            state={stepState(7)}
            inputs={inputs}
            set={set}
            testId={SIM.step7}
            countKey="l6PerL5"
            pkgKey="l6Package"
            countSliderId={SIM.l6PerL5Slider}
            countValueId={SIM.l6PerL5Value}
            parentLabel="Level 5 member"
            parentCount={inputs.directs * inputs.l2PerDirect * inputs.l3PerL2 * inputs.l4PerL3 * inputs.l5PerL4}
            onAdvance={() => configureAndAdvance(6)}
            onEdit={() => setActiveStep(7)}
          />
          <LevelStep
            step={8}
            level={7}
            state={stepState(8)}
            inputs={inputs}
            set={set}
            testId={SIM.step8}
            countKey="l7PerL6"
            pkgKey="l7Package"
            countSliderId={SIM.l7PerL6Slider}
            countValueId={SIM.l7PerL6Value}
            parentLabel="Level 6 member"
            parentCount={inputs.directs * inputs.l2PerDirect * inputs.l3PerL2 * inputs.l4PerL3 * inputs.l5PerL4 * inputs.l6PerL5}
            onAdvance={() => configureAndAdvance(7)}
            onEdit={() => setActiveStep(8)}
          />
          <LevelStep
            step={9}
            level={8}
            state={stepState(9)}
            inputs={inputs}
            set={set}
            testId={SIM.step9}
            countKey="l8PerL7"
            pkgKey="l8Package"
            countSliderId={SIM.l8PerL7Slider}
            countValueId={SIM.l8PerL7Value}
            parentLabel="Level 7 member"
            parentCount={inputs.directs * inputs.l2PerDirect * inputs.l3PerL2 * inputs.l4PerL3 * inputs.l5PerL4 * inputs.l6PerL5 * inputs.l7PerL6}
            onAdvance={() => configureAndAdvance(8)}
            onEdit={() => setActiveStep(9)}
          />
          <LevelStep
            step={10}
            level={9}
            state={stepState(10)}
            inputs={inputs}
            set={set}
            testId={SIM.step10}
            countKey="l9PerL8"
            pkgKey="l9Package"
            countSliderId={SIM.l9PerL8Slider}
            countValueId={SIM.l9PerL8Value}
            parentLabel="Level 8 member"
            parentCount={inputs.directs * inputs.l2PerDirect * inputs.l3PerL2 * inputs.l4PerL3 * inputs.l5PerL4 * inputs.l6PerL5 * inputs.l7PerL6 * inputs.l8PerL7}
            onAdvance={() => configureAndAdvance(9)}
            onEdit={() => setActiveStep(10)}
          />
          <LevelStep
            step={11}
            level={10}
            state={stepState(11)}
            inputs={inputs}
            set={set}
            testId={SIM.step11}
            countKey="l10PerL9"
            pkgKey="l10Package"
            countSliderId={SIM.l10PerL9Slider}
            countValueId={SIM.l10PerL9Value}
            parentLabel="Level 9 member"
            parentCount={inputs.directs * inputs.l2PerDirect * inputs.l3PerL2 * inputs.l4PerL3 * inputs.l5PerL4 * inputs.l6PerL5 * inputs.l7PerL6 * inputs.l8PerL7 * inputs.l9PerL8}
            onAdvance={() => configureAndAdvance(10)}
            onEdit={() => setActiveStep(11)}
            isLast
          />

          {/* Deeper toggle - available once L10 is configured */}
          {configuredLevels >= 10 && (
            <DeeperPanel
              open={deeperOpen}
              setOpen={setDeeperOpen}
              enabled={calculateDeeper}
              setEnabled={setCalculateDeeper}
              inputs={inputs}
              set={set}
              advancedOpen={advancedOpen}
              setAdvancedOpen={setAdvancedOpen}
            />
          )}

          {/* OrderFlow toggle */}
          {startedFlow && (
            <div className="glass rounded-2xl overflow-hidden">
              <button
                data-testid={SIM.orderFlowToggle}
                onClick={() => setShowOrderFlow((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.02] transition-colors"
              >
                <span className="flex items-center gap-2">
                  <Sparkle weight="bold" className="h-4 w-4 text-pink-400" />
                  <span className="text-sm text-slate-200">
                    See how one order pays you
                  </span>
                </span>
                <CaretDown
                  weight="bold"
                  className={`h-4 w-4 text-slate-400 transition-transform ${
                    showOrderFlow ? "rotate-180" : ""
                  }`}
                />
              </button>
            </div>
          )}
        </div>

        {/* RIGHT: Results */}
        <div className="lg:col-span-7">
          <div className="lg:sticky lg:top-6">
            <ResultsPanel result={result} empty={!result} />
          </div>
        </div>
      </div>

      {/* Order flow (below the fold) */}
      {startedFlow && showOrderFlow && (
        <div className="mt-6">
          <OrderFlow
            rankIdx={rankIdx}
            myPkgName={result.inputs.myPkg.name}
            downlinePkgId={inputs.downlinePackage}
          />
        </div>
      )}

      {/* DISCLAIMER */}
      <div className="mt-8 rounded-2xl p-5 border border-white/[0.06] bg-white/[0.02] text-xs text-slate-400 leading-relaxed">
        <span className="text-slate-200 font-medium">Illustrative projection.</span>{" "}
        Projections based on the current U.CENTER compensation plan and your
        assumptions — not a guarantee. Commissions are paid per qualifying order,
        not as a recurring salary. Rank advancement also requires two qualified
        legs (left + right), which this simulator does not enforce.
      </div>
    </div>
  );
}

/* -------------------- STEP 1: Starting Package -------------------- */
function StepOne({ state, inputs, set, onAdvance, onEdit }) {
  const pkg = inputs.myPackage ? packageById(inputs.myPackage) : null;
  const summary = pkg
    ? `${pkg.name} · ${fmtMoney(pkg.price)} · Starts at R${pkg.packageRankIdx + 1}`
    : "Choose a package";

  return (
    <StepCard
      step={1}
      testId={SIM.step1}
      state={state}
      title="Your starting package"
      description="Sets your starting rank and unlocks Initial Ripple tiers."
      summary={summary}
      onEdit={onEdit}
    >
      <PackagePicker
        value={inputs.myPackage}
        onChange={(id) => {
          // When a user picks their own package, seed the direct/L2/deeper
          // averages so results appear immediately (they can change later).
          set({
            myPackage: id,
            directPackage: id === "diamond" ? "platinum" : id,
            l2Package: "bronze",
            l3Package: "bronze",
            l4Package: "bronze",
            l5Package: "bronze",
            downlinePackage: "bronze",
          });
          setTimeout(onAdvance, 250);
        }}
      />
      {inputs.myPackage && (
        <div className="mt-4 text-[11px] text-slate-500">
          Selected: <span className="text-slate-300">{pkg.name}</span> · starts you at{" "}
          <span className="text-slate-300 font-mono">
            R{pkg.packageRankIdx + 1}
          </span>{" "}
          with{" "}
          <span className="text-slate-300 font-mono">{fmtNumber(pkg.points)}</span>{" "}
          starting shares.
        </div>
      )}
    </StepCard>
  );
}

/* -------------------- STEP 2: Level 1 recruits -------------------- */
function StepTwo({ state, inputs, set, onAdvance, onEdit }) {
  const directPkg = packageById(inputs.directPackage);
  const l1Volume = inputs.directs * directPkg.price;
  const summary = `${inputs.directs} directs on ${directPkg.name} · ${fmtMoney(l1Volume)} vol`;

  return (
    <StepCard
      step={2}
      testId={SIM.step2}
      state={state}
      title="Your Level 1 recruits"
      description="The people you personally bring in."
      summary={summary}
      onEdit={onEdit}
    >
      <div className="space-y-5">
        <div>
          <div className="flex items-baseline justify-between mb-3">
            <div className="text-xs text-slate-300">
              How many people will you personally recruit?
            </div>
            <div
              data-testid={SIM.directsValue}
              className="font-mono text-2xl font-semibold text-white"
            >
              {inputs.directs}
            </div>
          </div>
          <div className="brand-slider">
            <Slider
              data-testid={SIM.directsSlider}
              min={0}
              max={50}
              step={1}
              value={[inputs.directs]}
              onValueChange={([v]) => set({ directs: v })}
            />
          </div>
          <div className="mt-1 flex justify-between text-[10px] text-slate-500 font-mono">
            <span>0</span>
            <span>25</span>
            <span>50</span>
          </div>
        </div>

        <div>
          <div className="text-xs text-slate-300 mb-2">
            Average package your directs will buy
          </div>
          <PackagePicker
            value={inputs.directPackage}
            onChange={(id) => set({ directPackage: id })}
            compact
          />
        </div>

        <div className="rounded-lg bg-white/[0.03] border border-white/[0.05] px-3 py-2 text-[11px] text-slate-400 flex items-center justify-between">
          <span>Level 1 volume</span>
          <span className="font-mono text-slate-200">{fmtMoney(l1Volume)}</span>
        </div>

        <button
          onClick={onAdvance}
          className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-pink-500 to-orange-500 hover:opacity-95 shadow-lg shadow-pink-500/20"
        >
          Next: Level 2 team →
        </button>
      </div>
    </StepCard>
  );
}

/* -------------------- STEP 3: Level 2 fan-out -------------------- */
/* -------------------- Generic LevelStep for L2..L5 -------------------- */
function LevelStep({
  step, level, state, inputs, set,
  testId, countKey, pkgKey, countSliderId, countValueId,
  parentLabel, parentCount, onAdvance, onEdit, isLast,
}) {
  const pkg = packageById(inputs[pkgKey]);
  const perParent = inputs[countKey];
  const levelCount = parentCount * perParent;
  const levelVolume = levelCount * pkg.price;
  const summary = `${perParent}/${parentLabel} → ${fmtNumber(levelCount)} on ${pkg.name} · ${fmtMoney(levelVolume)} vol`;

  return (
    <StepCard
      step={step}
      testId={testId}
      state={state}
      title={`Level ${level} team`}
      description={`Recruits made by each Level ${level - 1} member.`}
      summary={summary}
      onEdit={onEdit}
    >
      <div className="space-y-5">
        <div>
          <div className="flex items-baseline justify-between mb-3">
            <div className="text-xs text-slate-300">
              On average, how many people does each {parentLabel} recruit?
            </div>
            <div
              data-testid={countValueId}
              className="font-mono text-2xl font-semibold text-white"
            >
              {perParent}
            </div>
          </div>
          <div className="brand-slider">
            <Slider
              data-testid={countSliderId}
              min={0}
              max={10}
              step={1}
              value={[perParent]}
              onValueChange={([v]) => set({ [countKey]: v })}
            />
          </div>
          <div className="mt-1 flex justify-between text-[10px] text-slate-500 font-mono">
            <span>0</span><span>5</span><span>10</span>
          </div>
        </div>

        <div>
          <div className="text-xs text-slate-300 mb-2">
            Average package for Level {level} members
          </div>
          <PackagePicker
            value={inputs[pkgKey]}
            onChange={(id) => set({ [pkgKey]: id })}
            compact
          />
        </div>

        <div className="rounded-lg bg-white/[0.03] border border-white/[0.05] px-3 py-2 text-[11px] text-slate-400 flex items-center justify-between">
          <span>Level {level} · {fmtNumber(levelCount)} members</span>
          <span className="font-mono text-slate-200">{fmtMoney(levelVolume)}</span>
        </div>

        <button
          onClick={onAdvance}
          className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-pink-500 to-orange-500 hover:opacity-95 shadow-lg shadow-pink-500/20"
        >
          {isLast ? "Done · calculate deeper?" : `Next: Level ${level + 1} →`}
        </button>
      </div>
    </StepCard>
  );
}

/* -------------------- Deeper toggle & panel (L6-L20 projection) -------------------- */
function DeeperPanel({
  open, setOpen, enabled, setEnabled, inputs, set,
  advancedOpen, setAdvancedOpen,
}) {
  const deepPkg = packageById(inputs.downlinePackage);
  return (
    <div className="glass rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3">
        <button
          data-testid={SIM.deeperToggle}
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 text-left flex-1 min-w-0"
        >
          <SlidersHorizontal weight="bold" className="h-4 w-4 text-purple-300" />
          <div className="min-w-0">
            <div className="text-sm text-slate-200">
              Calculate deeper (Levels 11-20)
            </div>
            <div className="text-[11px] text-slate-500 truncate">
              {enabled
                ? `On · ${deepPkg.name} · ${inputs.duplicationPct}% duplication`
                : "Off — results stop at Level 10"}
            </div>
          </div>
        </button>
        <div className="flex items-center gap-2">
          <button
            data-testid={SIM.deeperEnabled}
            onClick={() => setEnabled(!enabled)}
            className={`relative h-5 w-9 rounded-full transition-colors flex-shrink-0 ${
              enabled ? "bg-gradient-to-r from-pink-500 to-orange-500" : "bg-white/[0.08]"
            }`}
            aria-label="toggle deeper"
          >
            <span
              className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                enabled ? "translate-x-4" : "translate-x-0.5"
              }`}
            />
          </button>
          <CaretDown
            weight="bold"
            className={`h-4 w-4 text-slate-400 transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        </div>
      </div>

      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-white/[0.05] space-y-5">
          {!enabled && (
            <div className="text-[11px] text-slate-500 leading-relaxed">
              Turn the toggle on to project growth beyond Level 10 using
              duplication + branching. Off keeps the projection strictly to the
              members you configured above.
            </div>
          )}

          <div>
            <div className="text-xs text-slate-300 mb-2">
              Average package for Level 11+ members
            </div>
            <PackagePicker
              value={inputs.downlinePackage}
              onChange={(id) => set({ downlinePackage: id })}
              compact
            />
          </div>

          <div>
            <div className="flex items-baseline justify-between mb-3">
              <div>
                <div className="text-xs text-slate-300">Duplication rate</div>
                <div className="text-[10px] text-slate-500">
                  % of deeper team who also recruit
                </div>
              </div>
              <div
                data-testid={SIM.duplicationValue}
                className="font-mono text-xl font-semibold text-white"
              >
                {inputs.duplicationPct}
                <span className="text-slate-500 text-base">%</span>
              </div>
            </div>
            <div className="brand-slider">
              <Slider
                data-testid={SIM.duplicationSlider}
                min={0}
                max={100}
                step={5}
                value={[inputs.duplicationPct]}
                onValueChange={([v]) => set({ duplicationPct: v })}
              />
            </div>
          </div>

          <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
            <CollapsibleTrigger asChild>
              <button
                data-testid={SIM.advancedToggle}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-white/[0.02] transition-colors border border-white/[0.05]"
              >
                <span className="flex items-center gap-2">
                  <SlidersHorizontal weight="bold" className="h-3.5 w-3.5 text-pink-400" />
                  <span className="text-[11px] uppercase tracking-widest text-slate-300">
                    Advanced
                  </span>
                </span>
                <CaretDown
                  weight="bold"
                  className={`h-3.5 w-3.5 text-slate-400 transition-transform ${
                    advancedOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="pt-4 space-y-4">
                <AdvancedRow
                  label="Branching factor"
                  sub="Avg new members per active recruiter"
                  value={inputs.branchingFactor.toFixed(1)}
                  testId={SIM.branchValue}
                >
                  <Slider
                    data-testid={SIM.branchSlider}
                    min={0.5}
                    max={6.0}
                    step={0.1}
                    value={[inputs.branchingFactor]}
                    onValueChange={([v]) => set({ branchingFactor: v })}
                  />
                </AdvancedRow>
                <AdvancedRow
                  label="Retention rate"
                  sub="Fraction of members who stay active"
                  value={`${Math.round(inputs.retentionRate * 100)}%`}
                  testId={SIM.retentionValue}
                >
                  <Slider
                    data-testid={SIM.retentionSlider}
                    min={0.3}
                    max={1.0}
                    step={0.01}
                    value={[inputs.retentionRate]}
                    onValueChange={([v]) => set({ retentionRate: v })}
                  />
                </AdvancedRow>
                <button
                  data-testid={SIM.advancedReset}
                  onClick={() =>
                    set({
                      duplicationMultiplier: DEFAULT_ADVANCED.duplicationMultiplier,
                      branchingFactor: DEFAULT_ADVANCED.branchingFactor,
                      retentionRate: DEFAULT_ADVANCED.retentionRate,
                    })
                  }
                  className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[11px] text-slate-300 border border-white/10 hover:border-white/25 hover:bg-white/[0.03]"
                >
                  <ArrowCounterClockwise weight="bold" className="h-3 w-3" />
                  Reset advanced
                </button>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      )}
    </div>
  );
}

function AdvancedRow({ label, sub, value, testId, children }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-400">
            {label}
          </div>
          <div className="text-[10px] text-slate-500">{sub}</div>
        </div>
        <div data-testid={testId} className="font-mono text-sm font-semibold text-white">
          {value}
        </div>
      </div>
      <div className="brand-slider">{children}</div>
    </div>
  );
}
